import { useState, useEffect, useRef, useMemo } from "react";
import { io } from "socket.io-client";
import {
  Box,
  Paper,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  getChatContactsApi,
  getMessagesApi,
  sendMessageApi,
  markConversationReadApi,
  startNewConversationApi,
  uploadChatMediaApi,
} from "../../../api/chat.api";
import Sidebar from "./components/Sidebar";
import ChatWindow from "./components/ChatWindow";
import EmptyChatState from "./components/EmptyChatState";
import ChatSkeleton from "./components/ChatSkeleton";
import CreateTemplateView from "./components/CreateTemplateView";
import { useParams, useNavigate } from "react-router-dom";

const socket = io(process.env.REACT_APP_SOCKET_URL, {
  withCredentials: true,
});

const ChatApp = () => {
  const parentTheme = useTheme();

  // Build chat theme inheriting primary color from the app's dynamic theme
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: parentTheme.palette.primary.main },
          secondary: { main: "#10b981" },
          background: { default: "#f4f6f8", paper: "#ffffff" },
          text: {
            primary: "#1e293b",
            secondary: "#64748b",
          },
        },
        shape: { borderRadius: 16 },
        typography: {
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        },
      }),
    [parentTheme.palette.primary.main],
  );

  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef();
  const [messagesLoading, setMessagesLoading] = useState(false);

  // View state for switching between chat inbox and template creation
  const [currentView, setCurrentView] = useState("inbox"); // "inbox" | "createTemplate" | "manageTemplates"

  // Lifted new chat dialog state so EmptyChatState can open it
  const [newChatOpen, setNewChatOpen] = useState(false);

  const { id: routeConversationId } = useParams();
  const navigate = useNavigate();

  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  // Handlers for template navigation within Chat flow
  const handleCreateTemplate = () => {
    navigate(`/chat/templates/create`);
    // setCurrentView("createTemplate");
  };

  const handleManageTemplates = () => {
    navigate(`/chat/templates`);
    // setCurrentView("manageTemplates");
  };

  const handleBackToInbox = () => {
    setCurrentView("inbox");
  };

  // Socket connection management
  useEffect(() => {
    // Connect socket
    if (!socket.connected) {
      socket.connect();
    }

    const handleConnect = () => {
      console.log("Socket connected:", socket.id);
    };

    const handleDisconnect = () => {
      console.log("Socket disconnected");
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      await fetchContacts();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const handleNewMessage = (data) => {
      const { conversationId, message, conversation: conversationData } = data;

      // Add message to current conversation if selected
      if (selectedContact?._id === conversationId) {
        setMessages((prev) => {
          const exists = prev.find((m) => m._id === message._id);
          return exists ? prev : [...prev, message];
        });
        markConversationReadApi(conversationId);
      }

      // Update contacts list - handle NEW conversations too
      setContacts((prevContacts) => {
        const existingContact = prevContacts.find(
          (c) => c._id === conversationId,
        );

        if (existingContact) {
          // Update existing contact
          const updated = prevContacts.map((c) => {
            if (c._id === conversationId) {
              return {
                ...c,
                last_message: message.text,
                last_message_at:
                  message.created_at ||
                  message.timestamp ||
                  message.createdAt ||
                  new Date().toISOString(),
                unread_count:
                  selectedContact?._id === conversationId
                    ? 0
                    : (c.unread_count || 0) + 1,
              };
            }
            return c;
          });
          return [...updated].sort(
            (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at),
          );
        } else {
          // NEW conversation - add it to the list
          // Use conversation data from server if available, otherwise construct from message
          const newContact = conversationData || {
            _id: conversationId,
            phone: message.whatsapp?.phone || message.phone || "",
            client_name:
              message.client_name ||
              message.sender_name ||
              message.phone ||
              "Unknown",
            last_message: message.text,
            last_message_at:
              message.created_at ||
              message.timestamp ||
              message.createdAt ||
              new Date().toISOString(),
            unread_count: selectedContact?._id === conversationId ? 0 : 1,
            whatsapp: message.whatsapp || {},
          };
          return [newContact, ...prevContacts].sort(
            (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at),
          );
        }
      });
    };

    const handleStatusUpdate = (data) => {
      const { messageId, conversationId, status } = data;
      console.log("Status update received:", {
        messageId,
        conversationId,
        status,
      });

      // Update message status in current conversation
      if (selectedContact?._id === conversationId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId
              ? { ...msg, whatsapp: { ...msg.whatsapp, status } }
              : msg,
          ),
        );
        console.log("Updated message status in current conversation");
      }
    };

    socket.on("chat:new-message", handleNewMessage);
    socket.on("chat:status-update", handleStatusUpdate);

    return () => {
      socket.off("chat:new-message", handleNewMessage);
      socket.off("chat:status-update", handleStatusUpdate);
    };
  }, [selectedContact?._id]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!routeConversationId || contacts.length === 0) return;

    const contact = contacts.find((c) => c._id === routeConversationId);

    if (contact && selectedContact?._id !== contact._id) {
      selectContact(contact);
    }
  }, [routeConversationId, contacts]);

  const fetchContacts = async () => {
    try {
      const res = await getChatContactsApi();
      setContacts(res.data.items || []);
    } catch (err) {
      console.error(err);
    }
  };

  const selectContact = async (contact) => {
    if (selectedContact?._id === contact._id) return;

    navigate(`/chat/inbox/${contact._id}`);

    setSelectedContact(contact);
    setMessages([]);
    setMessagesLoading(true);

    try {
      if (contact._id) {
        const res = await getMessagesApi(contact._id);
        setMessages(res.data.data || []);

        if (contact.unread_count > 0) {
          await markConversationReadApi(contact._id);
          setContacts((prev) =>
            prev.map((c) =>
              c._id === contact._id ? { ...c, unread_count: 0 } : c,
            ),
          );
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const onSend = async (data) => {
    // Handle both text-only and media messages
    const { text, file, audio, replyTo } = data;

    if (!selectedContact?._id) return;

    // If there's media, upload it first
    let mediaUrl = null;
    let mediaData = null;
    let messageType = "TEXT";

    if (file) {
      try {
        // Upload file to Cloudinary
        const uploadRes = await uploadChatMediaApi(file.raw);
        const uploadData = uploadRes.data.data || uploadRes.data;
        mediaUrl = uploadData.url;
        mediaData = uploadData;

        // Determine message type based on file type
        if (file.type.startsWith("image/")) {
          messageType = "IMAGE";
        } else if (file.type.startsWith("audio/")) {
          messageType = "AUDIO";
        } else if (file.type.startsWith("video/")) {
          messageType = "VIDEO";
        } else {
          messageType = "DOCUMENT";
        }
      } catch (err) {
        console.error("Failed to upload media:", err);
        // Continue with text only if upload fails
      }
    } else if (audio) {
      // Handle audio recording
      try {
        // Convert audio blob URL to file
        const response = await fetch(audio);
        const blob = await response.blob();
        const audioFile = new File([blob], "audio.webm", { type: "audio/webm" });

        const uploadRes = await uploadChatMediaApi(audioFile);
        const uploadData = uploadRes.data.data || uploadRes.data;
        mediaUrl = uploadData.url;
        mediaData = uploadData;
        messageType = "AUDIO";
      } catch (err) {
        console.error("Failed to upload audio:", err);
      }
    }

    // Check if there's anything to send
    if (!text?.trim() && !mediaUrl) return;

    const payload = {
      text: text || "",
      messageType,
      mediaUrl,
      mediaData,
      caption: text || "",
    };

    try {
      setText("");
      const res = await sendMessageApi(selectedContact._id, payload);
      const newMessage = res.data.data || res.data;

      setMessages((prev) => {
        const exists = prev.find((m) => m._id === newMessage._id);
        return exists ? prev : [...prev, newMessage];
      });

      setContacts((prevContacts) => {
        const updated = prevContacts.map((c) => {
          if (c._id === selectedContact._id) {
            return {
              ...c,
              last_message: newMessage.text || (messageType === "IMAGE" ? "📷 Image" : "Media"),
              last_message_at:
                newMessage.created_at || new Date().toISOString(),
            };
          }
          return c;
        });
        return [...updated].sort(
          (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at),
        );
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const formatTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";

    const now = new Date();

    const isSameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    const isYesterday =
      d.getFullYear() === yesterday.getFullYear() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getDate() === yesterday.getDate();

    if (isSameDay) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    if (isYesterday) return "Yesterday";

    return d.toLocaleDateString([], { day: "2-digit", month: "short" });
  };

  const goBackToList = () => {
    setSelectedContact(null);
    setMessages([]);
    navigate("/chat/inbox");
  };

  // Update contact name after lead creation
  const updateContactName = (contactId, newName) => {
    // Update in contacts list (for sidebar)
    setContacts((prev) =>
      prev.map((c) => {
        const cId = c._id || c.id;
        return cId === contactId ? { ...c, client_name: newName } : c;
      }),
    );
    // Update selected contact (for header and profile)
    setSelectedContact((prev) => {
      if (!prev) return prev;
      const prevId = prev._id || prev.id;
      return prevId === contactId ? { ...prev, client_name: newName } : prev;
    });
  };

  // Start new conversation with phone number
  const onStartNewChat = async (phone) => {
    try {
      // Check if conversation already exists for this phone
      const existingContact = contacts.find((c) => c.phone === phone);
      if (existingContact) {
        selectContact(existingContact);
        return;
      }

      // Start new conversation via API
      const res = await startNewConversationApi({ phone });
      const newConversation = res.data.data || res.data;

      // Add to contacts list
      const contactWithPhone = {
        ...newConversation,
        phone,
        unread_count: 0,
        last_message: "",
        last_message_at: new Date().toISOString(),
      };

      setContacts((prev) => [contactWithPhone, ...prev]);
      selectContact(contactWithPhone);
    } catch (err) {
      console.error("Failed to start new chat:", err);
      // If API fails, still create a temporary contact
      const tempContact = {
        _id: `temp-${Date.now()}`,
        phone,
        unread_count: 0,
        last_message: "",
        last_message_at: new Date().toISOString(),
      };
      setContacts((prev) => [tempContact, ...prev]);
      selectContact(tempContact);
    }
  };

  const showChatPageOnMobile = !isDesktop && !!selectedContact;

  useEffect(() => {
    if (!routeConversationId) {
      setSelectedContact(null);
      setMessages([]);
      setText("");
      setMessagesLoading(false);
    }
  }, [routeConversationId]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          bgcolor: "#f4f6f8",
          p: { xs: 0, md: 2 },
          pt: { xs: 0, md: 0 },
          boxSizing: "border-box",
          gap: 2,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ flex: 1 }}>
            <ChatSkeleton />
          </Box>
        ) : (
          <>
            {isDesktop ? (
              <>
                {/* Full Page Template View - Desktop */}
                {currentView === "createTemplate" ||
                currentView === "manageTemplates" ? (
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                      border: "1px solid rgba(0, 0, 0, 0.04)",
                      overflow: "hidden",
                      zIndex: 1,
                    }}
                  >
                    <CreateTemplateView
                      onBack={handleBackToInbox}
                      isManageMode={currentView === "manageTemplates"}
                    />
                  </Paper>
                ) : (
                  <>
                    {/* Sidebar Panel - Desktop */}
                    <Paper
                      elevation={0}
                      sx={{
                        width: 380,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "20px",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                        border: "1px solid rgba(0, 0, 0, 0.04)",
                        overflow: "hidden",
                        zIndex: 1,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      <Sidebar
                        contacts={contacts}
                        selectedId={selectedContact?._id}
                        onSelect={selectContact}
                        getInitials={getInitials}
                        formatTime={formatTime}
                        onStartNewChat={onStartNewChat}
                        onCreateTemplate={handleCreateTemplate}
                        onManageTemplates={handleManageTemplates}
                        newChatOpen={newChatOpen}
                        setNewChatOpen={setNewChatOpen}
                      />
                    </Paper>

                    {/* Chat Window Panel - Desktop */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "20px",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                        border: "1px solid rgba(0, 0, 0, 0.04)",
                        overflow: "hidden",
                        zIndex: 1,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      {selectedContact ? (
                        <ChatWindow
                          contact={selectedContact}
                          messages={messages}
                          text={text}
                          setText={setText}
                          onSend={onSend}
                          scrollRef={scrollRef}
                          getInitials={getInitials}
                          loading={messagesLoading}
                          onBack={null}
                          onUpdateContactName={updateContactName}
                        />
                      ) : (
                        <EmptyChatState onStartNewChat={() => setNewChatOpen(true)} />
                      )}
                    </Paper>
                  </>
                )}
              </>
            ) : (
              <>
                {/* Full Page Template View - Mobile */}
                {currentView === "createTemplate" ||
                currentView === "manageTemplates" ? (
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: { xs: 0, sm: "20px" },
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                      overflow: "hidden",
                      zIndex: 1,
                    }}
                  >
                    <CreateTemplateView
                      onBack={handleBackToInbox}
                      isManageMode={currentView === "manageTemplates"}
                    />
                  </Paper>
                ) : (
                  <>
                    {/* Sidebar Panel - Mobile */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        display: showChatPageOnMobile ? "none" : "flex",
                        flexDirection: "column",
                        borderRadius: { xs: 0, sm: "20px" },
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                        overflow: "hidden",
                        zIndex: 1,
                      }}
                    >
                      <Sidebar
                        contacts={contacts}
                        selectedId={selectedContact?._id}
                        onSelect={selectContact}
                        getInitials={getInitials}
                        formatTime={formatTime}
                        onStartNewChat={onStartNewChat}
                        onCreateTemplate={handleCreateTemplate}
                        onManageTemplates={handleManageTemplates}
                        newChatOpen={newChatOpen}
                        setNewChatOpen={setNewChatOpen}
                      />
                    </Paper>

                    {/* Chat Window Panel - Mobile */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        display: showChatPageOnMobile ? "flex" : "none",
                        flexDirection: "column",
                        borderRadius: { xs: 0, sm: "20px" },
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
                        overflow: "hidden",
                        zIndex: 1,
                      }}
                    >
                      {selectedContact ? (
                        <ChatWindow
                          contact={selectedContact}
                          messages={messages}
                          text={text}
                          setText={setText}
                          onSend={onSend}
                          scrollRef={scrollRef}
                          getInitials={getInitials}
                          loading={messagesLoading}
                          onBack={goBackToList}
                          onUpdateContactName={updateContactName}
                        />
                      ) : (
                        <EmptyChatState onStartNewChat={() => setNewChatOpen(true)} />
                      )}
                    </Paper>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ChatApp;
